import React from 'react';
import { useTranslation } from 'react-i18next';
import { getCurrentLang } from '../../../../../i18n/i18n';
import { formatCurrency } from '../../../../../utils';

type Props = {
    // What this line is priced at on the booking (the #1734 snapshot).
    bookedPrice?: number | null;
    // The service's price in the asset catalog today, or null/undefined when the
    // service is no longer in the catalog — then there is nothing to compare to.
    currentPrice?: number | null;
};

/**
 * Names the current catalog price beside a booked line whose price has changed
 * since (#1735), so an operator seeing 50 € on a booking while the price list
 * says 60 € doesn't have to wonder which one is wrong.
 *
 * Deliberately muted and neutral in both directions: a booked price below
 * today's is as ordinary as one above, and once a tenant has adjusted prices a
 * few times most historical bookings carry this hint — it is the normal state,
 * not a warning.
 */
const CurrentPriceHint: React.FC<Props> = ({ bookedPrice, currentPrice }) => {
    const { t } = useTranslation();

    if (
        bookedPrice === undefined ||
        bookedPrice === null ||
        currentPrice === undefined ||
        currentPrice === null
    ) {
        return null;
    }
    // Half-cent tolerance: both sides travel through Decimal -> number, so an
    // exact !== would light up the hint on values that render identically.
    if (Math.abs(currentPrice - bookedPrice) < 0.005) {
        return null;
    }

    return (
        <span className={'text-foreground/60'}>
            {' '}
            ·{' '}
            {t('booking_details.current_catalog_price', {
                price: formatCurrency(currentPrice, getCurrentLang()),
            })}
        </span>
    );
};

export default CurrentPriceHint;
